import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'ayed.theme';

/**
 * The initial value is already on `<html>` — `index.html` sets it inline before
 * the bundle parses, so there is no flash. This hook reads what is there rather
 * than deciding again, which is what keeps the two in agreement.
 */
function current(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(current);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((previous) => {
      const next = previous === 'dark' ? 'light' : 'dark';

      // A stored value also opts out of the system preference from here on: a
      // person who picked light at noon does not want the panel flipping on
      // them at sunset.
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Private browsing, or storage disabled. The theme still applies for
        // this tab; only the memory of it is lost.
      }

      return next;
    });
  }, []);

  return [theme, toggle];
}
